import { getLocalisedText } from "../../app/helpers/helper";
import FolderIcon from "../../assets/icons/folder-icon.svg";
import LeftArrowIcon from "../../assets/icons/LeftArrowIcon";
import RightArrowIcon from "../../assets/icons/RightArrowIcon";
import DefaultProfileImage from "../../assets/img/Profile.png";
import { CONTENT_TYPE } from "../../app/constants";
import EmptyScreen from "../common/EmptyScreen";
import Loader from "../common/Loader";
import LoaderWithText from "../common/LoaderWithText";
import gluedin from "gluedin-shorts-js";
import queryString from "query-string";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

const LIMIT = 10;

const pickContentUrlThumbnail = (video: any) => {
  const contentUrls = video?.contentUrls ?? video?.metakeys?.contentUrls;
  if (!Array.isArray(contentUrls) || contentUrls.length === 0) return undefined;

  const entry =
    contentUrls.find(
      (item: any) =>
        String(item?.type || "").toLowerCase() === "image" &&
        Array.isArray(item?.urls) &&
        item.urls.length > 0
    ) ||
    contentUrls.find(
      (item: any) => Array.isArray(item?.urls) && item.urls.length > 0
    ) ||
    contentUrls.find((item: any) => typeof item === "string") ||
    contentUrls[0];

  if (typeof entry === "string") return entry;
  if (Array.isArray(entry?.urls) && entry.urls.length > 0) {
    return entry.urls[0];
  }

  return undefined;
};

const getTimelineThumbnailUrl = (video: any) => {
  const direct =
    video?.metakeys?.thumbnailUrls?.[0] ??
    video?.metakeys?.thumbnailUrl ??
    video?.thumbnailUrls?.[0] ??
    video?.thumbnailUrl;
  if (typeof direct === "string" && direct.length > 0) return direct;
  const contentType =
    video?.metakeys?.contentType ?? video?.contentType ?? "";
  const mediaType =
    video?.metakeys?.primaryMedia?.type ??
    video?.primaryMedia?.type ??
    video?.metakeys?.mediaType ??
    video?.mediaType ??
    "";
  const resolvedType = String(contentType || mediaType).toLowerCase();
  if (resolvedType !== "image") return direct;
  return pickContentUrlThumbnail(video);
};

function ActivityTimeline() {
  const language = localStorage.getItem("defaultLanguage");
  const { t } = useTranslation();
  const [timelineDetails, setTimelineDetails]: any = useState(null);
  const [currentTabData, setCurrentTabData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState({
    tab: false,
    page: false,
    more: false,
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const observer = useRef<IntersectionObserver>();
  const prevTabRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tab = searchParams.get("tab") || "like";

  useEffect(() => {
    if (prevTabRef.current === tab) return;
    prevTabRef.current = tab;
    setPage(1);
    setHasMore(true);
    setCurrentTabData([]);
    fetchTabData(tab, 1);
  }, [tab]);

  useEffect(() => {
    if (page <= 1) return;
    fetchTabData(tab, page);
  }, [page]);

  useEffect(() => {
    fetchVideos();
  }, []);

  const lastElementRef = useCallback(
    (node: any) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading.more &&
          !isLoading.tab
        ) {
          setPage((prevPage) => prevPage + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [hasMore, isLoading.more, isLoading.tab]
  );

  const getVideoKey = (video: any) =>
    video?.metakeys?.videoId ?? video?.videoId ?? video?._id ?? video?.id;

  const handleContentDetail = (contentInfo: any) => {
    const clickedId = getVideoKey(contentInfo);
    if (!clickedId) return;
    const index = currentTabData?.findIndex(
      (item: any) => getVideoKey(item) === clickedId
    );
    const offset = index >= 0 ? Math.floor(index / LIMIT) + 1 : 1;
    navigate(
      `/content/${clickedId}?contentType=${CONTENT_TYPE.ACTIVITY}&clickedVideoId=${clickedId}&offset=${offset}&limit=${LIMIT}&tab=${tab}`
    );
  };

  const handleClickOnTab = (tab: any) => {
    navigate(location.pathname + "?" + queryString.stringify({ tab }));
  };

  async function fetchVideos() {
    setIsLoading((prev) => ({ ...prev, page: true }));
    try {
      const activityTimelineModuleObj = new gluedin.GluedInActivityTimeline();
      const activityTimelineList =
        await activityTimelineModuleObj.getActivityTimelineUserDetails({
          event: "post",
          limit: LIMIT,
          offset: 1,
        });
      if (activityTimelineList.status === 200) {
        const activityTimelineInfo = activityTimelineList.data.result;
        setTimelineDetails(activityTimelineInfo);
      }
      setIsLoading((prev) => ({ ...prev, page: false }));
    } catch (error) {
      setIsLoading((prev) => ({ ...prev, page: false }));
      console.error(error);
    }
  }

  async function fetchTabData(tab: any, offset: number) {
    if (offset !== 1 && !hasMore) return;
    setIsLoading((prev) => ({
      ...prev,
      tab: offset === 1,
      more: offset > 1,
    }));
    try {
      const activityTimelineModuleObj = new gluedin.GluedInActivityTimeline();
      const activityTimelineVideoList =
        await activityTimelineModuleObj.getActivityTimelineList({
          event: tab,
          limit: LIMIT,
          offset: offset,
        });
      if (activityTimelineVideoList.data.statusCode === 2001) {
        const videos = activityTimelineVideoList.data.result || [];
        setCurrentTabData((prevData: any[]) => {
          if (offset === 1) return videos;
          const existingKeys = new Set(
            prevData.map((item) => getVideoKey(item)).filter(Boolean)
          );
          const deduped = videos.filter((item: any) => {
            const key = getVideoKey(item);
            if (!key) return true;
            if (existingKeys.has(key)) return false;
            existingKeys.add(key);
            return true;
          });
          return [...prevData, ...deduped];
        });
        setHasMore(videos.length >= LIMIT);
      } else {
        if (offset === 1) {
          setCurrentTabData([]);
        }
        setHasMore(false);
      }
      setIsLoading((prev) => ({ ...prev, tab: false, more: false }));
    } catch (error) {
      setHasMore(false);
      setIsLoading((prev) => ({ ...prev, tab: false, more: false }));
      console.error(error);
    }
  }

  if (isLoading.page) return <Loader />;

  return (
    <div className="full-box profile-full-box">
      <div className="profile-page-header">
        <a href="./my-profile" className="back-btn-a">
          {language === "en" ? <LeftArrowIcon /> : <RightArrowIcon />}
          {t("back-btn")}
        </a>
        <h5>{t("text-timeline")}</h5>
      </div>

      <div className="profile-page-head">
        <ul className="profile-page-head-ul list-none">
          <li className="profile-page-head-avatar">
            <div className="img-sec">
              <img
                src={
                  timelineDetails?.profileImageUrl ||
                  DefaultProfileImage
                }
                id="profileImage"
                className="bg-img-02 profileImage"
                alt="ProfileImage"
              />
            </div>
          </li>

          <li className="profile-page-head-content">
            <ul className="profile-page-head-content-inner">
              <li className="profile-page-info">
                <h4 id="displayName">
                  {getLocalisedText(timelineDetails, "fullName")}
                </h4>
                <h5 id="userId">{timelineDetails?.userName}</h5>
                <div className="desk-none">
                  <a href="#" className="res-edit">
                    {t("btn-edit")}
                  </a>
                </div>
              </li>

              <ul className="profile-page-list">
                <li
                  className={tab === "like" ? "active" : ""}
                  onClick={() => handleClickOnTab("like")}
                >
                  <h5>{t("profile-liked")}</h5>
                  <span id="likedCount">{timelineDetails?.likeCount}</span>
                </li>

                <li
                  className={tab === "comment" ? "active" : ""}
                  onClick={() => handleClickOnTab("comment")}
                >
                  <h5>{t("profile-comment")}</h5>
                  <span id="commentCount">{timelineDetails?.commentCount}</span>
                </li>

                <li
                  className={tab === "share" ? "active" : ""}
                  onClick={() => handleClickOnTab("share")}
                >
                  <h5>{t("profile-shared")}</h5>
                  <span id="sharedCount">
                    {timelineDetails?.shareCount ?? 0}
                  </span>
                </li>
                <li className="report-user-button" style={{ display: "none" }}>
                  <button
                    className="report"
                    id="report-user"
                    data-action="user"
                  >
                    {t("btn-report")}
                  </button>
                </li>
              </ul>
            </ul>
            <div className="modal fade" id="reportUserModel" role="dialog">
              <div className="modal-dialog">
                <div className="modal-content text-center">
                  <div className="modal-body">
                    <h5>
                      <strong>{t("post-report")}</strong>
                    </h5>
                    <p>{t("text-PostReport")}</p>
                    <div className="reason-wrpr">
                      <form action="" method="post" id="report-user-form">
                        <div id="report-user-content"></div>
                        <button className="submit-btn btn">
                          {t("btn-submit")}
                        </button>
                        <button className="cancel-btn btn">
                          {t("btn-cancel")}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>

      {isLoading.tab ? (
        <LoaderWithText center={false} />
      ) : (
        <div className="inner-box arrival-box profile-videos">
          <div className="tab-content userVideoData" id="tab1">
            {currentTabData.length ? (
              currentTabData.map((video: any, index) => {
                const thumbnailUrl = getTimelineThumbnailUrl(video);
                const videoId = getVideoKey(video);
                const isLast = index === currentTabData.length - 1;
                return (
                  <div
                    className="box"
                    key={videoId ?? index}
                    ref={isLast ? lastElementRef : undefined}
                  >
                    <div
                      className="img-box open-video-detail"
                      id={videoId}
                      style={{
                        background: `url(${thumbnailUrl}) center`,
                        borderRadius: "8px",
                        backgroundSize: "cover",
                      }}
                      onClick={() => handleContentDetail(video)}
                    >
                      <span className="av-icon">
                        <img src={FolderIcon} alt="" />
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <EmptyScreen title={t("text-noData")} fullHeight={true} />
            )}
            {isLoading.more && <LoaderWithText center={false} />}
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityTimeline;
